import { z } from "zod";
import { LossTypeSchema } from "./common.js";
import { CharacteristicValuesInputSchema } from "./batches.js";

const endAfterStart = (d: {
  plannedStartAt?: string | null;
  expectedEnd?: string | null;
}): boolean =>
  !(d.plannedStartAt && d.expectedEnd) || new Date(d.expectedEnd) > new Date(d.plannedStartAt);

const endAfterStartIssue = {
  message: "O fim planejado deve ser posterior ao início planejado.",
  path: ["expectedEnd"],
};

// Start ad-hoc passa a criar uma OP "Em execução": o início é sempre AGORA (servidor),
// então `plannedStartAt` deixa de ser entrada do usuário e o `expectedEnd` (fim previsto) vira
// OBRIGATÓRIO (a OP precisa de janela p/ a timeline). Refine: fim > agora.
export const StartProductionRequestSchema = z
  .object({
    productIds: z.array(z.string().uuid()).min(1),
    // Spec 081 — a meta acompanha o produzido: comparar decimal com inteiro faria a aderência
    // discordar de si mesma no primeiro produto contado em caixa.
    plannedQty: z.number().positive().finite(),
    expectedEnd: z.string().datetime({ message: "Informe o fim previsto." }),
    responsible: z.string().max(120).optional(),
    observations: z.string().max(1000).optional(),
    // Spec 084 — características da batelada iniciada pela API. Obrigatórias da linha cobradas no
    // service, que conhece as linhas de marcação.
    characteristics: CharacteristicValuesInputSchema.optional(),
    // Spec 084 — as bateladas de ORIGEM confirmadas no início (FR-008a). AUSENTE não é lista vazia:
    // ausente é "ninguém decidiu" e vira origem pendente quando a linha tem montante; `[]` é a
    // decisão "não veio de lugar nenhum". Os dois estados precisam ser distintos na tela.
    originRunIds: z.array(z.string().uuid()).max(20).optional(),
  })
  .refine((d) => new Date(d.expectedEnd).getTime() > Date.now(), {
    message: "O fim previsto deve ser posterior ao horário atual.",
    path: ["expectedEnd"],
  });
export type StartProductionRequest = z.infer<typeof StartProductionRequestSchema>;

export const UpdatePlannedTimesRequestSchema = z
  .object({
    plannedStartAt: z.string().datetime().nullable().optional(),
    expectedEnd: z.string().datetime().nullable().optional(),
  })
  .refine((data) => data.plannedStartAt !== undefined || data.expectedEnd !== undefined, {
    message: "Informe ao menos plannedStartAt ou expectedEnd.",
  })
  .refine(endAfterStart, endAfterStartIssue);
export type UpdatePlannedTimesRequest = z.infer<typeof UpdatePlannedTimesRequestSchema>;

// Spec 081 (FR-027) — quantidade aceita FRAÇÃO: um refugo medido por sensor em pacote de meio
// quilo, convertido para a unidade da linha, não é inteiro. `.int()` sai; positivo continua.
export const ProductionLossSchema = z.object({
  lossType: LossTypeSchema,
  quantity: z.number().positive().finite(),
  reason: z.string().max(500).optional(),
  machineId: z.string().uuid().optional(),
  // Spec 082 — DE QUAL PRODUTO é a perda. Opcional porque toda perda registrada até aqui, e toda
  // perda digitada sem escolher produto, continua válida. Sem ele, três colunas de perda da mesma
  // natureza em gramaturas diferentes colapsam numa só.
  productId: z.string().uuid().optional(),
});

export const RegisterLossRequestSchema = ProductionLossSchema;
export type RegisterLossRequest = z.infer<typeof RegisterLossRequestSchema>;

export const FinishProductionRequestSchema = z.object({
  producedQty: z.number().nonnegative().finite(),
  observations: z.string().max(1000).optional(),
  losses: z.array(ProductionLossSchema).max(50).optional(),
});
export type FinishProductionRequest = z.infer<typeof FinishProductionRequestSchema>;

export const ManualEntryRequestSchema = z
  .object({
    producedQty: z.number().nonnegative().finite().optional(),
    rejectedQty: z.number().nonnegative().finite().optional(),
    // Spec 081 US4 (FR-029) — quantidade POR PRODUTO na execução, para a linha que NÃO é medida por
    // máquina. O artefato histórico a importar é fato de linha, sem repartição por máquina;
    // atribuí-lo a uma máquina escolhida fabricaria o OEE por máquina de todo o período.
    products: z
      .array(
        z.object({
          productId: z.string().uuid(),
          quantity: z.number().nonnegative().finite(),
        })
      )
      .max(50)
      .optional(),
    observation: z.string().max(1000).optional(),
    referenceAt: z.string().datetime().optional(),
  })
  .refine(
    (v) =>
      v.producedQty !== undefined || v.rejectedQty !== undefined || v.observation !== undefined,
    { message: "Informe ao menos uma quantidade ou observação." }
  );
export type ManualEntryRequest = z.infer<typeof ManualEntryRequestSchema>;

export const PauseProductionRequestSchema = z
  .object({
    observation: z.string().max(1000).optional(),
  })
  .partial()
  .optional();
export type PauseProductionRequest = z.infer<typeof PauseProductionRequestSchema>;
